/**
 * Tab Stickies API Service
 *
 * API client for tab-specific sticky notes with PostgreSQL persistence.
 * Supports CRUD operations and bulk sync for offline-first functionality.
 */

import { apiClient, handleApiError } from './apiClient';
import {
  TabStickyNote,
  TabStickyCreateRequest,
  TabStickyUpdateRequest,
  TabStickiesListResponse,
  TabStickyBulkSyncResponse,
  TabStickySuccessResponse,
  TAB_STICKIES_API,
} from '../types/tabStickies';
import { logger } from '../utils/logger';

/**
 * Get all sticky notes for a specific tab
 *
 * @param tabPath - Tab route path (e.g., "/app/epg")
 * @param userId - User identifier (default: "default")
 * @returns List of sticky notes for the tab
 */
export async function getStickiesForTab(
  tabPath: string,
  userId: string = 'default'
): Promise<TabStickyNote[]> {
  try {
    const response = await apiClient.get<TabStickiesListResponse>(TAB_STICKIES_API.BASE, {
      params: { tab_path: tabPath, user_id: userId },
    });

    logger.log(`[TabStickiesService] Fetched ${response.data.stickies.length} stickies for ${tabPath}`);
    return response.data.stickies;
  } catch (error) {
    const message = handleApiError(error);
    logger.error(`[TabStickiesService] Failed to fetch stickies for ${tabPath}:`, message);
    throw new Error(message);
  }
}

/**
 * Create a new sticky note
 *
 * @param sticky - Sticky note creation data
 * @param userId - User identifier (default: "default")
 * @returns Created sticky note
 */
export async function createSticky(
  sticky: TabStickyCreateRequest,
  userId: string = 'default'
): Promise<TabStickyNote> {
  try {
    const response = await apiClient.post<TabStickyNote>(TAB_STICKIES_API.BASE, sticky, {
      params: { user_id: userId },
    });

    logger.log(`[TabStickiesService] Created sticky ${response.data.id} for ${sticky.tab_path}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    logger.error('[TabStickiesService] Failed to create sticky:', message);
    throw new Error(message);
  }
}

/**
 * Update an existing sticky note
 *
 * @param stickyId - Sticky note UUID
 * @param updates - Fields to update
 * @param userId - User identifier (default: "default")
 * @returns Success response with updated ID
 */
export async function updateSticky(
  stickyId: string,
  updates: TabStickyUpdateRequest,
  userId: string = 'default'
): Promise<TabStickySuccessResponse> {
  try {
    const response = await apiClient.put<TabStickySuccessResponse>(
      `${TAB_STICKIES_API.BASE}/${stickyId}`,
      updates,
      { params: { user_id: userId } }
    );

    logger.log(`[TabStickiesService] Updated sticky ${stickyId}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    logger.error(`[TabStickiesService] Failed to update sticky ${stickyId}:`, message);
    throw new Error(message);
  }
}

/**
 * Delete a sticky note
 *
 * @param stickyId - Sticky note UUID
 * @param userId - User identifier (default: "default")
 * @returns Success response
 */
export async function deleteSticky(
  stickyId: string,
  userId: string = 'default'
): Promise<TabStickySuccessResponse> {
  try {
    const response = await apiClient.delete<TabStickySuccessResponse>(
      `${TAB_STICKIES_API.BASE}/${stickyId}`,
      { params: { user_id: userId } }
    );

    logger.log(`[TabStickiesService] Deleted sticky ${stickyId}`);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    logger.error(`[TabStickiesService] Failed to delete sticky ${stickyId}:`, message);
    throw new Error(message);
  }
}

/**
 * Bulk sync multiple sticky notes (upsert)
 *
 * For offline-first support - syncs local state to server.
 * Items with existing IDs are updated, new items are created.
 *
 * @param stickies - Array of sticky notes to sync
 * @param userId - User identifier (default: "default")
 * @returns Sync statistics
 */
export async function bulkSync(
  stickies: Partial<TabStickyNote>[],
  userId: string = 'default'
): Promise<TabStickyBulkSyncResponse> {
  try {
    const response = await apiClient.post<TabStickyBulkSyncResponse>(
      TAB_STICKIES_API.SYNC,
      { stickies },
      { params: { user_id: userId } }
    );

    logger.log(`[TabStickiesService] Bulk sync complete:`, response.data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    logger.error('[TabStickiesService] Bulk sync failed:', message);
    throw new Error(message);
  }
}

/**
 * Get a single sticky note by ID
 *
 * @param stickyId - Sticky note UUID
 * @param userId - User identifier (default: "default")
 * @returns Sticky note or null if not found
 */
export async function getStickyById(
  stickyId: string,
  userId: string = 'default'
): Promise<TabStickyNote | null> {
  try {
    const response = await apiClient.get<TabStickyNote>(
      `${TAB_STICKIES_API.BASE}/${stickyId}`,
      { params: { user_id: userId } }
    );

    return response.data;
  } catch (error) {
    // Return null for 404 (not found)
    if ((error as { response?: { status?: number } }).response?.status === 404) {
      return null;
    }

    const message = handleApiError(error);
    logger.error(`[TabStickiesService] Failed to get sticky ${stickyId}:`, message);
    throw new Error(message);
  }
}

/**
 * Tab Stickies API Service object
 * Provides all tab stickies operations
 */
export const tabStickiesService = {
  getStickiesForTab,
  createSticky,
  updateSticky,
  deleteSticky,
  bulkSync,
  getStickyById,
};

export default tabStickiesService;
